import { useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { createAvatarSessionState } from '../../session';
import type {
  AvatarLiveSessionKind,
  AvatarSessionContext,
  AvatarSessionState,
  AvatarTransportKind
} from '../../session';
import { createLiveSessionContext } from '../../session/liveMode';
import type { LiveModeClient } from '../../session/liveMode';
import type { RuntimeSessionStreamClient } from '../../session/runtimeSessionStream';

import type { RequestLiveSessionFn } from './useLiveSessionRequest';

export interface HostedLivePromptOption {
  label: string;
  value: string;
}

export interface HostedLivePromptField {
  name: string;
  type: 'text' | 'number' | 'date' | 'phone' | 'email' | 'currency' | 'time' | 'yes_no' | 'select';
  label: string;
  placeholder?: string;
  options?: Array<{ label: string; value: string }>;
  validation?: {
    required?: boolean;
    min?: number;
    max?: number;
    pattern?: string;
    minLength?: number;
    maxLength?: number;
  };
}

export interface HostedLiveRefs {
  liveAudioSyncAnimationRef: MutableRefObject<number | null>;
  liveAudioSyncDurationRef: MutableRefObject<number>;
  liveAudioSyncStartTimeRef: MutableRefObject<number>;
  liveAutoListenArmedRef: MutableRefObject<boolean>;
  liveAutoListenPendingRef: MutableRefObject<boolean>;
  liveAutoListenTimerRef: MutableRefObject<number | null>;
  liveAvatarModelIdRef: MutableRefObject<string | null>;
  liveClientRef: MutableRefObject<LiveModeClient | null>;
  liveManualHangupRef: MutableRefObject<boolean>;
  livePlaybackCountdownRef: MutableRefObject<number | null>;
  livePromptAudioDedupRef: MutableRefObject<{ key: string | null; timestamp: number }>;
  livePromptAudioRequestRef: MutableRefObject<number>;
  livePromptListeningRef: MutableRefObject<boolean>;
  livePromptPlaybackUnlockedRef: MutableRefObject<boolean>;
  livePromptTextRef: MutableRefObject<string>;
  liveRetryCountRef: MutableRefObject<number>;
  liveRetryTimerRef: MutableRefObject<number | null>;
  liveSessionContextRef: MutableRefObject<AvatarSessionContext | null>;
  liveSessionStreamRef: MutableRefObject<RuntimeSessionStreamClient | null>;
  liveSpeakingRef: MutableRefObject<boolean>;
  liveSpeakingTimeoutRef: MutableRefObject<number | null>;
  liveThinkingRef: MutableRefObject<boolean>;
  liveVoiceAttemptRef: MutableRefObject<number>;
  liveVoicePromptStartedRef: MutableRefObject<boolean>;
  liveVoiceRetryCountRef: MutableRefObject<number>;
  liveVoiceRetryTimerRef: MutableRefObject<number | null>;
  requestLiveSessionRef: MutableRefObject<RequestLiveSessionFn | null>;
}

export interface UseHostedLiveStateOptions {
  builderDiagramId?: string;
  handoffTokenCamlin: string;
  initialLiveConnectRequested?: boolean;
  initialSessionContext?: Partial<AvatarSessionContext> | AvatarSessionContext | null;
  isVisualOnly: boolean;
  liveModeRequested: boolean;
  noSip: boolean;
  projectTokenCamlin: string;
  queryParams: URLSearchParams;
  requestedLiveSessionKind?: AvatarLiveSessionKind;
}

export interface UseHostedLiveStateReturn {
  linkedAvatarConfig: Record<string, unknown> | null;
  linkedAvatarModelId: string | null;
  liveAssistantText: string;
  liveChatEventId: number;
  liveChatText: string;
  liveConnectRequested: boolean;
  liveConnectionError: string | null;
  livePromptFields: HostedLivePromptField[];
  livePromptOptions: HostedLivePromptOption[];
  liveSessionContext: AvatarSessionContext | null;
  liveSessionState: AvatarSessionState | null;
  liveTranscriptEventId: number;
  liveUserTranscript: string;
  liveUserTranscriptFinal: boolean;
  refs: HostedLiveRefs;
  setLinkedAvatarConfig: Dispatch<SetStateAction<Record<string, unknown> | null>>;
  setLinkedAvatarModelId: Dispatch<SetStateAction<string | null>>;
  setLiveAssistantText: Dispatch<SetStateAction<string>>;
  setLiveChatEventId: Dispatch<SetStateAction<number>>;
  setLiveChatText: Dispatch<SetStateAction<string>>;
  setLiveConnectRequested: Dispatch<SetStateAction<boolean>>;
  setLiveConnectionError: Dispatch<SetStateAction<string | null>>;
  setLivePromptFields: Dispatch<SetStateAction<HostedLivePromptField[]>>;
  setLivePromptOptions: Dispatch<SetStateAction<HostedLivePromptOption[]>>;
  setLiveSessionContext: Dispatch<SetStateAction<AvatarSessionContext | null>>;
  setLiveSessionState: Dispatch<SetStateAction<AvatarSessionState | null>>;
  setLiveTranscriptEventId: Dispatch<SetStateAction<number>>;
  setLiveUserTranscript: Dispatch<SetStateAction<string>>;
  setLiveUserTranscriptFinal: Dispatch<SetStateAction<boolean>>;
}

function readSessionContextMetadata(
  context?: Partial<AvatarSessionContext> | AvatarSessionContext | null
): Record<string, unknown> {
  if (!context?.metadata || typeof context.metadata !== 'object') {
    return {};
  }

  return context.metadata as Record<string, unknown>;
}

function hasSeedSessionContext(
  context?: Partial<AvatarSessionContext> | AvatarSessionContext | null
): boolean {
  if (!context) {
    return false;
  }

  return Boolean(
    context.projectToken ||
    context.diagramId ||
    context.handoffToken ||
    context.sessionId ||
    context.tenantId ||
    context.userId ||
    Object.keys(readSessionContextMetadata(context)).length > 0
  );
}

function buildSessionContextSnapshot(
  context?: Partial<AvatarSessionContext> | AvatarSessionContext | null
): string {
  if (!context) {
    return 'null';
  }

  return JSON.stringify({
    projectToken: context.projectToken ?? null,
    diagramId: context.diagramId ?? null,
    handoffToken: context.handoffToken ?? null,
    sessionId: context.sessionId ?? null,
    tenantId: context.tenantId ?? null,
    userId: context.userId ?? null,
    metadata: readSessionContextMetadata(context)
  });
}

export function useHostedLiveState({
  builderDiagramId,
  handoffTokenCamlin,
  initialLiveConnectRequested,
  initialSessionContext,
  isVisualOnly,
  liveModeRequested,
  noSip,
  projectTokenCamlin,
  queryParams,
  requestedLiveSessionKind
}: UseHostedLiveStateOptions): UseHostedLiveStateReturn {
  const initialLiveSessionContext = useMemo<AvatarSessionContext | null>(() => {
    const seededContext = initialSessionContext ?? null;
    const explicitSessionId = queryParams.get('sessionId') || seededContext?.sessionId || undefined;
    if (!liveModeRequested && !explicitSessionId && !hasSeedSessionContext(seededContext)) {
      return null;
    }

    return createLiveSessionContext({
      ...(seededContext ?? {}),
      projectToken: seededContext?.projectToken ?? (projectTokenCamlin || undefined),
      diagramId: seededContext?.diagramId ?? (builderDiagramId || undefined),
      handoffToken: seededContext?.handoffToken
        ?? (liveModeRequested ? handoffTokenCamlin || undefined : undefined),
      sessionId: explicitSessionId,
      tenantId: seededContext?.tenantId,
      userId: queryParams.get('userId') || seededContext?.userId || undefined,
      metadata: liveModeRequested ? {
        ...readSessionContextMetadata(seededContext),
        liveMode: true,
        ...(requestedLiveSessionKind ? { liveSessionKind: requestedLiveSessionKind } : {}),
        ...(requestedLiveSessionKind === 'contextual-chat' ? { contextualLive: true } : {})
      } : readSessionContextMetadata(seededContext)
    }, builderDiagramId);
  }, [
    builderDiagramId,
    handoffTokenCamlin,
    initialSessionContext,
    liveModeRequested,
    projectTokenCamlin,
    queryParams,
    requestedLiveSessionKind
  ]);
  const hostSessionContextSnapshot = useMemo(
    () => buildSessionContextSnapshot(initialLiveSessionContext),
    [initialLiveSessionContext]
  );

  const initialLiveModeActive = liveModeRequested
    || initialLiveSessionContext?.channel === 'live'
    || initialLiveSessionContext?.metadata?.liveMode === true;
  const effectiveNoSip = initialLiveModeActive ? isVisualOnly : (noSip || isVisualOnly);
  const initialLiveTransport = (effectiveNoSip ? 'websocket' : 'webrtc') as AvatarTransportKind;
  const initialLiveVoiceCallEnabled = initialLiveModeActive && !effectiveNoSip;

  const [liveSessionContext, setLiveSessionContext] = useState<AvatarSessionContext | null>(() => initialLiveSessionContext);
  const [liveSessionState, setLiveSessionState] = useState<AvatarSessionState | null>(() => (
    initialLiveModeActive && initialLiveSessionContext
      ? createAvatarSessionState(initialLiveSessionContext, {
        mode: 'live',
        transport: initialLiveTransport,
        status: 'idle'
      })
      : null
  ));
  const [liveConnectRequested, setLiveConnectRequested] = useState(
    typeof initialLiveConnectRequested === 'boolean'
      ? initialLiveConnectRequested
      : Boolean(initialLiveModeActive && initialLiveSessionContext?.handoffToken && !initialLiveVoiceCallEnabled)
  );
  const [liveConnectionError, setLiveConnectionError] = useState<string | null>(null);

  const liveClientRef = useRef<LiveModeClient | null>(null);
  const liveSessionStreamRef = useRef<RuntimeSessionStreamClient | null>(null);
  const liveSessionContextRef = useRef<AvatarSessionContext | null>(liveSessionContext);
  const lastAppliedHostSessionContextRef = useRef(hostSessionContextSnapshot);
  const liveRetryTimerRef = useRef<number | null>(null);
  const liveRetryCountRef = useRef(0);
  const liveVoiceRetryTimerRef = useRef<number | null>(null);
  const liveVoiceRetryCountRef = useRef(0);
  const liveVoiceAttemptRef = useRef(0);
  const liveAvatarModelIdRef = useRef<string | null>(null);
  const liveVoicePromptStartedRef = useRef(false);
  const liveManualHangupRef = useRef(false);
  const requestLiveSessionRef = useRef<RequestLiveSessionFn | null>(null);
  const livePromptTextRef = useRef('Ready');
  const livePlaybackCountdownRef = useRef<number | null>(null);
  const livePromptListeningRef = useRef(false);
  const liveSpeakingRef = useRef(false);
  const liveThinkingRef = useRef(false);
  const liveSpeakingTimeoutRef = useRef<number | null>(null);
  const liveAudioSyncAnimationRef = useRef<number | null>(null);
  const liveAudioSyncStartTimeRef = useRef(0);
  const liveAudioSyncDurationRef = useRef(0);
  const livePromptAudioRequestRef = useRef(0);
  const livePromptAudioDedupRef = useRef<{ key: string | null; timestamp: number }>({
    key: null,
    timestamp: 0
  });
  const liveAutoListenArmedRef = useRef(false);
  const livePromptPlaybackUnlockedRef = useRef(false);
  const liveAutoListenPendingRef = useRef(false);
  const liveAutoListenTimerRef = useRef<number | null>(null);

  const [linkedAvatarModelId, setLinkedAvatarModelId] = useState<string | null>(null);
  const [linkedAvatarConfig, setLinkedAvatarConfig] = useState<Record<string, unknown> | null>(null);

  const [liveAssistantText, setLiveAssistantText] = useState('');
  const [liveChatText, setLiveChatText] = useState('');
  const [liveChatEventId, setLiveChatEventId] = useState(0);
  const [livePromptFields, setLivePromptFields] = useState<HostedLivePromptField[]>([]);
  const [livePromptOptions, setLivePromptOptions] = useState<HostedLivePromptOption[]>([]);
  const [liveUserTranscript, setLiveUserTranscript] = useState('');
  const [liveUserTranscriptFinal, setLiveUserTranscriptFinal] = useState(false);
  const [liveTranscriptEventId, setLiveTranscriptEventId] = useState(0);

  useEffect(() => {
    liveSessionContextRef.current = liveSessionContext;
  }, [liveSessionContext]);

  useEffect(() => {
    if (!initialLiveSessionContext) {
      lastAppliedHostSessionContextRef.current = hostSessionContextSnapshot;
      return;
    }

    if (lastAppliedHostSessionContextRef.current === hostSessionContextSnapshot) {
      return;
    }
    lastAppliedHostSessionContextRef.current = hostSessionContextSnapshot;

    const mergedContext = createLiveSessionContext({
      ...(liveSessionContext ?? {}),
      ...initialLiveSessionContext,
      projectToken: initialLiveSessionContext.projectToken
        ?? liveSessionContext?.projectToken
        ?? projectTokenCamlin
        ?? undefined,
      diagramId: initialLiveSessionContext.diagramId
        ?? liveSessionContext?.diagramId
        ?? builderDiagramId
        ?? undefined,
      handoffToken: initialLiveSessionContext.handoffToken
        ?? liveSessionContext?.handoffToken
        ?? (liveModeRequested ? handoffTokenCamlin || undefined : undefined),
      sessionId: initialLiveSessionContext.sessionId
        ?? liveSessionContext?.sessionId
        ?? undefined,
      tenantId: initialLiveSessionContext.tenantId ?? liveSessionContext?.tenantId,
      userId: initialLiveSessionContext.userId
        ?? liveSessionContext?.userId
        ?? queryParams.get('userId')
        ?? undefined,
      metadata: {
        ...readSessionContextMetadata(liveSessionContext),
        ...readSessionContextMetadata(initialLiveSessionContext),
        liveMode: true,
        ...(requestedLiveSessionKind ? { liveSessionKind: requestedLiveSessionKind } : {}),
        ...(requestedLiveSessionKind === 'contextual-chat' ? { contextualLive: true } : {})
      }
    }, builderDiagramId);

    if (buildSessionContextSnapshot(liveSessionContext) === buildSessionContextSnapshot(mergedContext)) {
      return;
    }

    setLiveSessionContext(mergedContext);
    setLiveSessionState((currentState) => (
      currentState
        ? { ...currentState, context: mergedContext }
        : currentState
    ));
  }, [
    builderDiagramId,
    handoffTokenCamlin,
    hostSessionContextSnapshot,
    initialLiveSessionContext,
    liveModeRequested,
    liveSessionContext,
    projectTokenCamlin,
    queryParams,
    requestedLiveSessionKind
  ]);

  return {
    linkedAvatarConfig,
    linkedAvatarModelId,
    liveAssistantText,
    liveChatEventId,
    liveChatText,
    liveConnectRequested,
    liveConnectionError,
    livePromptFields,
    livePromptOptions,
    liveSessionContext,
    liveSessionState,
    liveTranscriptEventId,
    liveUserTranscript,
    liveUserTranscriptFinal,
    refs: {
      liveAudioSyncAnimationRef,
      liveAudioSyncDurationRef,
      liveAudioSyncStartTimeRef,
      liveAutoListenArmedRef,
      liveAutoListenPendingRef,
      liveAutoListenTimerRef,
      liveAvatarModelIdRef,
      liveClientRef,
      liveManualHangupRef,
      livePlaybackCountdownRef,
      livePromptAudioDedupRef,
      livePromptAudioRequestRef,
      livePromptListeningRef,
      livePromptPlaybackUnlockedRef,
      livePromptTextRef,
      liveRetryCountRef,
      liveRetryTimerRef,
      liveSessionContextRef,
      liveSessionStreamRef,
      liveSpeakingRef,
      liveSpeakingTimeoutRef,
      liveThinkingRef,
      liveVoiceAttemptRef,
      liveVoicePromptStartedRef,
      liveVoiceRetryCountRef,
      liveVoiceRetryTimerRef,
      requestLiveSessionRef
    },
    setLinkedAvatarConfig,
    setLinkedAvatarModelId,
    setLiveAssistantText,
    setLiveChatEventId,
    setLiveChatText,
    setLiveConnectRequested,
    setLiveConnectionError,
    setLivePromptFields,
    setLivePromptOptions,
    setLiveSessionContext,
    setLiveSessionState,
    setLiveTranscriptEventId,
    setLiveUserTranscript,
    setLiveUserTranscriptFinal
  };
}

export default useHostedLiveState;
